import { createSelector, select } from '@ngrx/store';
import { filter, map, pipe, skipWhile } from 'rxjs';
import { AppState, Delayed, Role, UserPreferences } from '../lemans-app.model';
import { RoleNode, ServiceRoleAssignment, UserRoleHierarchy } from '../user-access';

export const selectUserDataFeature = (state: AppState): UserPreferences => state.userData;
export const selectUserData = createSelector(selectUserDataFeature, (state: UserPreferences) => state);
export const selectUserName = createSelector(selectUserDataFeature, (state: UserPreferences) => state.name);
export const selectResourceRoles = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.resourceRoles,
);
export const selectResources = createSelector(selectUserDataFeature, (state: UserPreferences) => state.resources);
const selectManageableResources = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.manageableResources,
);

// from https://ngrx.io/guide/store/selectors#solution-extracting-a-pipeable-operator
export const manageableResources = pipe(
  select(selectManageableResources),
  filter((resources) => resources.isReady),
  map((resources) => resources.value),
);

const selectUserPermissionsEvalResult = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.userPermissionsEvalResult,
);

export const userPermissionsEvalResult = pipe(
  select(selectUserPermissionsEvalResult),
  filter((result) => result.isReady),
  map((result) => result.value),
);

export const selectTenantUsers = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.selectedTenantUsers,
);

export const selectUserResourceRoles = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.userResourceRoles,
);
export const selectEditUser = createSelector(selectUserDataFeature, (state: UserPreferences) => state.editUser);
export const selectUserID = createSelector(selectUserDataFeature, (state: UserPreferences) => state.userId);
// TO DO: add prefix to fix the rule
/* eslint-disable @ngrx/prefix-selectors-with-select */
export const hasBusinessPartnerId = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.businessPartnerId,
);
export const currentTenantId = createSelector(selectUserDataFeature, (state: UserPreferences) => state.currentTenantId);
export const preferredLanguage = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.preferences?.language,
);
export const newTenantCreated = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.newTenantCreated,
);
export const selectCurrentTenantContext = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.currentTenantContext,
);

export const currentTenantContext = pipe(
  select(selectCurrentTenantContext),
  filter((context) => context.isReady),
  map((context) => context.value),
);

export const currentTenant = createSelector(selectUserDataFeature, (state: UserPreferences) => state.currentTenant);
export const suggestedTenant = createSelector(selectUserDataFeature, (state: UserPreferences) => state.suggestedTenant);

export const noSuggestedTenant = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.noSuggestedTenant,
);
export const accessibleTenants = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.accessibleTenants,
);
export const effectiveRoles = createSelector(selectUserDataFeature, (state: UserPreferences) => state.effectiveRoles);

export const notifications = createSelector(selectUserDataFeature, (state: UserPreferences) => state.notifications);
export const notificationCounts = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.notificationCounts,
);
export const notificationDeleted = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.notificationDeleted,
);
export const notificationMarkedRead = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.notificationMarkedRead,
);
export const selectServices = createSelector(selectUserDataFeature, (state: UserPreferences) => state.services);
export const selectTenantUtilityTokenBalance = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.tenantUtilityTokens?.tokenBalance,
);
export const selectTenantUtilityTokenEntitlements = createSelector(selectUserDataFeature, (state: UserPreferences) =>
  state.tenantUtilityTokens?.tokenEntitlements.concat(state.tenantUtilityTokens?.disabledTokenEntitlements),
);
export const selectPendingServices = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.currentTenant?.pendingServices,
);
export const selectCustomProvisioningState = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state?.currentCustomProvisioningData,
);
export const selectPreferences = createSelector(selectUserDataFeature, (state: UserPreferences) => state.preferences);
export const selectExitFromProvisioning = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.exitFromProvisioning,
);

export const selectFavoriteApps = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.preferences?.favoriteApps || {},
);

export const selectRefreshUserEntitlements = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.refreshUserEntitlementsGrid,
);
export const selectRefreshOrgEntitlements = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.refreshOrgEntitlementsGrid,
);
export const selectRefreshRolesGrid = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.refreshUserRolesGrid,
);
export const selectRefreshTenantListGrid = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.refreshTenantListGrid,
);

export const selectUnallocatedEntitlementsCount = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) =>
    state.unallocatedEntitlementsCount || {
      entitlementCounts: [],
      totalCreditsEntitlementCount: 0,
      totalEntitlementCount: 0,
    },
);

export const selectAllocateEntitlementResponse = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.allocateEntitlementResponse,
);

export const selectCanChangeTenantHierarchy = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.canChangeTenantHierarchy,
);

export const selectUserMembership = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.userMembership,
);

export const selectHasAnyServiceRole = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.hasAnyServiceRole,
);

const selectTenantResourceHierarchy = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.tenantResourceHierarchy,
);
export const tenantResourceHierarchy = pipe(
  select(selectTenantResourceHierarchy),
  skipWhile((it) => it.isPending),
  map((it) => it.value),
);

const selectUserRoleHierarchy = createSelector(
  selectUserDataFeature,
  (state: UserPreferences) => state.userRoleHierarchy,
);
/**
 * Selector to compute a Set of resource IDs where user has Owner or Admin role
 * Traverses the roles hierarchy and collects all resource IDs with effective Owner/Admin roles
 */
export const selectManageableResourceIds = pipe(
  select(selectUserRoleHierarchy),
  filter((hierarchy) => hierarchy.isReady),
  map((userRoleHierarchy: Delayed<UserRoleHierarchy>) => {
    const modifiableIds = new Set<string>();

    const collectModifiableIds = (tenantNode: RoleNode): void => {
      // Check current resource
      if (tenantNode.effectiveRole === Role.Owner || tenantNode.effectiveRole === Role.Admin) {
        modifiableIds.add(tenantNode.id);
      }

      // Check services
      tenantNode.services?.forEach((service: ServiceRoleAssignment) => {
        if (service.effectiveRole === Role.Owner || service.effectiveRole === Role.Admin) {
          modifiableIds.add(service.id);
        }
      });

      // Recursively check children
      tenantNode.children?.forEach((child: RoleNode) => {
        collectModifiableIds(child);
      });
    };
    collectModifiableIds(userRoleHierarchy.value.roleHierarchy);
    return modifiableIds;
  }),
);

export const hasAnyServiceRole = pipe(
  select(selectHasAnyServiceRole),
  skipWhile((it) => it.isPending),
  filter((hasAnyServiceRole) => hasAnyServiceRole.isReady),
  map((hasAnyServiceRole) => hasAnyServiceRole.value),
);
